import Modal from 'LuxKit/Modal/Modal'
import Table from 'LuxKit/Tables/Table'
import TableBody from 'LuxKit/Tables/TableBody'
import TableBodyCell from 'LuxKit/Tables/TableBodyCell'
import TableBodyRow from 'LuxKit/Tables/TableBodyRow'
import TableHeader from 'LuxKit/Tables/TableHeader'
import TableHeaderCell from 'LuxKit/Tables/TableHeaderCell'
import TableHeaderRow from 'LuxKit/Tables/TableHeaderRow'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Colour from 'components/utils/Colour'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import Group from 'components/utils/Group'
import useLoyaltyPointsEarnCalculator from 'loyalty/hooks/useLoyaltyPointsEarnCalculator'
import determineLoyaltyBonusPointsCampaignType, { isBonusPointsCampaignType, isStatusCreditsCampaignType } from 'loyalty/lib/determineLoyaltyBonusPointsCampaignType'
import getLoyaltyProductTypeIcon from 'loyalty/lib/getLoyaltyProductTypeIcon'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import React, { useMemo, useState } from 'react'
import FormatLoyaltyPoints from '../Formatters/FormatLoyaltyPoints'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import TabButton from 'LuxKit/Tabs/TabButton'

type ProductType = App.LoyaltyProductType

const productTypesThatAccrueNights: Array<ProductType> = [
  // hotels
  'hotel_lpc',
  'hotel_lpp_eps',
  'hotel_flash',
  'rental',
  // tours
  'le_tour',
  'tour_bank',
  'trusted_partner_tour',
  // cruises
  'cruise_group_1',
  'cruise_group_2',
  'cruise_group_3',
]

function getItemTitle(productType: App.LoyaltyProductType | undefined): string {
  switch (productType) {
    case 'dom_economy':
    case 'dom_business':
    case 'int_economy':
    case 'int_business':
    case 'int_first':
    case 'int_premium_economy':
      return 'Flight'
    case 'tour_bank':
    case 'le_tour':
    case 'trusted_partner_tour':
      return 'Tour'
    case 'tour_optional_extra':
      return 'Tour optional extras'
    case 'cfmr':
    case 'insurance':
      return 'Insurance/Coverage'
    case 'cruise_group_1':
    case 'cruise_group_2':
    case 'cruise_group_3':
      return 'Cruise'
    case 'experience':
      return 'Experience'
    case 'car_hire':
      return 'CarHire'
    case 'lux_plus':
      return LUXURY_PLUS.PROGRAM_NAME
    case 'shop_tier_1':
    case 'shop_tier_2':
    case 'shop_tier_3':
    case 'shop_tier_4':
    case 'shop_tier_5':
    case 'shop_shipping':
      return 'Shop'
    case 'hotel_flash':
    case 'hotel_lpc':
    case 'hotel_lpp_eps':
    case 'rental':
      return 'Room'
    default:
      return ''
  }
}

function shouldShowNights(productType: App.LoyaltyProductType | undefined): boolean {
  if (!productType) return false
  return productTypesThatAccrueNights.includes(productType)
}

interface Props {
  itemTitle?: string
  calculationRequests?: Array<App.LoyaltyPointsEarnCalculationRequest | undefined> | App.LoyaltyPointsEarnCalculationRequest | undefined
}

function LoyaltyPointsEarnBreakdownModal({ itemTitle, calculationRequests }: Props) {
  const [activeTab, setActiveTab] = useState<'summary' | 'how-it-works'>('summary')
  const { pointsCalculations, totalPointsCalculation } = useLoyaltyPointsEarnCalculator(calculationRequests)
  const bonusPointsCampaigns = totalPointsCalculation.data ? determineLoyaltyBonusPointsCampaignType(totalPointsCalculation.data) : []
  const isBonusPointsCampaign = bonusPointsCampaigns.some(campaign => isBonusPointsCampaignType(campaign))
  const isStatusCreditsCampaign = bonusPointsCampaigns.some(campaign => isStatusCreditsCampaignType(campaign))
  const pointsCalculationsToDisplay = useMemo(() => {
    return pointsCalculations.filter(calculation => {
      return calculation?.data && (calculation.data.basePoints || calculation.data.bonusPoints || calculation.data.statusCredits || calculation.data.bonusStatusCredits)
    })
  }, [pointsCalculations])
  const hasNightsColumn = useMemo(() => {
    return pointsCalculationsToDisplay.some(calculation =>
      calculation?.data?.request.nights && shouldShowNights(calculation.data.request.loyaltyProductType),
    )
  }, [pointsCalculationsToDisplay])

  return <Modal
    title="Earning points, status credits & nights"
  >
    <Group direction="vertical" gap={16}>

      <TabButtonsGroup fit="grow">
        <TabButton
          size="medium"
          isActive={activeTab === 'summary'}
          onClick={() => setActiveTab('summary')}
        >
          Summary
        </TabButton>
        <TabButton
          size="medium"
          isActive={activeTab === 'how-it-works'}
          onClick={() => setActiveTab('how-it-works')}
        >
          How it works
        </TabButton>
      </TabButtonsGroup>

      {activeTab === 'summary' && <>
        <BodyText variant="medium" colour="neutral-two">Below is a breakdown of the points, status credits and nights earned for each item in your cart.</BodyText>
        <Table>
          <TableHeader>
            <TableHeaderRow>
              <TableHeaderCell>Package</TableHeaderCell>
              <TableHeaderCell minWidth={80}>Points</TableHeaderCell>
              {isBonusPointsCampaign && <TableHeaderCell minWidth={92}><Colour value="highlight-secondary">Bonus Points</Colour></TableHeaderCell>}
              <TableHeaderCell>Status Credits</TableHeaderCell>
              {isStatusCreditsCampaign && <TableHeaderCell minWidth={92}><Colour value="highlight-secondary">Bonus Status Credits</Colour></TableHeaderCell>}
              {hasNightsColumn && <TableHeaderCell minWidth={60}>Nights</TableHeaderCell>}
            </TableHeaderRow>
          </TableHeader>
          <TableBody>
            {pointsCalculationsToDisplay.map((calculation, index) => {
              const Icon = getLoyaltyProductTypeIcon(calculation?.data?.request.loyaltyProductType)
              const nights = calculation?.data?.request.nights
              const showNightsForThisRow = nights && shouldShowNights(calculation?.data?.request.loyaltyProductType)
              return <TableBodyRow key={index}>
                <TableBodyCell>
                  <BodyTextBlock variant="small" startIcon={<Icon />}>
                    {itemTitle ?? getItemTitle(calculation?.data?.request.loyaltyProductType)}
                  </BodyTextBlock>
                </TableBodyCell>
                <TableBodyCell>
                  {!!calculation?.data?.basePoints && <FormatLoyaltyPoints abbreviated points={calculation.data.basePoints} />}
                  {!calculation?.data?.basePoints && <>--</>}
                </TableBodyCell>
                {isBonusPointsCampaign && <TableBodyCell>
                  {!!calculation?.data?.bonusPoints && <Colour value="highlight-secondary"><FormatLoyaltyPoints abbreviated points={calculation.data.bonusPoints} /></Colour>}
                  {!calculation?.data?.bonusPoints && <>--</>}
                </TableBodyCell>}
                <TableBodyCell>
                  {!!calculation?.data?.statusCredits && <FormatDecimal value={calculation.data.statusCredits} />}
                  {!calculation?.data?.statusCredits && <>--</>}
                </TableBodyCell>
                {isStatusCreditsCampaign && <TableBodyCell>
                  {!!calculation?.data?.bonusStatusCredits && <Colour value="highlight-secondary"><FormatDecimal value={calculation.data.bonusStatusCredits} /></Colour>}
                  {!calculation?.data?.bonusStatusCredits && <>--</>}
                </TableBodyCell>}
                {hasNightsColumn && <TableBodyCell>
                  {showNightsForThisRow ? nights : '--'}
                </TableBodyCell>}
              </TableBodyRow>
            })}
          </TableBody>
        </Table>
        <BodyText variant="small" colour="neutral-two">
          Please note that the final points balance will be credited to your account at the end of your trip{' '}
          or 30-40 days after your Shop order is confirmed, and may be different from what you see here.
        </BodyText>
        <BodyText variant="small" colour="neutral-two">
          You will not earn points on any portion or whole item that is paid for with points. Points can only be earned{' '}
          on the remaining dollar value. Some payments types are not eligible for earning points such as promotional codes,{' '}
          credits received before {getLoyaltyProgramDisplayName()} launched, credits received due to cancellation of a booking before {getLoyaltyProgramDisplayName()} launched.
        </BodyText>
      </>}

      {activeTab === 'how-it-works' && (
        <Group direction="vertical" gap={16}>
          <BodyText variant="medium">
            As a member of {getLoyaltyProgramDisplayName()}, you can earn points, status credits and nights on eligible bookings.
          </BodyText>

          <BodyText variant="medium" weight="bold">{getLoyaltyProgramDisplayName()} Points</BodyText>
          <BodyText variant="medium">
            Earn and redeem points on hotels, tours, flights, cruises and more.
          </BodyText>

          <BodyText variant="medium" weight="bold">{getLoyaltyProgramDisplayName()} Status Credits & Nights</BodyText>
          <BodyText variant="medium">
            Collect status credits and nights to move up tiers and unlock rewards including room upgrades, airport transfers, lounge passes and more.
          </BodyText>

          <Table>
            <TableHeader>
              <TableHeaderRow>
                <TableHeaderCell>Tier</TableHeaderCell>
                <TableHeaderCell>Status Credits</TableHeaderCell>
                <TableHeaderCell>Nights</TableHeaderCell>
              </TableHeaderRow>
            </TableHeader>
            <TableBody>
              <TableBodyRow>
                <TableBodyCell>Silver</TableBodyCell>
                <TableBodyCell>200</TableBodyCell>
                <TableBodyCell>10</TableBodyCell>
              </TableBodyRow>
              <TableBodyRow>
                <TableBodyCell>Gold</TableBodyCell>
                <TableBodyCell>600</TableBodyCell>
                <TableBodyCell>20</TableBodyCell>
              </TableBodyRow>
              <TableBodyRow>
                <TableBodyCell>Platinum</TableBodyCell>
                <TableBodyCell>1000</TableBodyCell>
                <TableBodyCell>30</TableBodyCell>
              </TableBodyRow>
            </TableBody>
          </Table>
        </Group>
      )}
    </Group>
  </Modal>
}

export default LoyaltyPointsEarnBreakdownModal
